'use client'

// Edit — or create — the connected wallet's Twetch profile from /alkanes.
//
// One screen, one button. The form is always there; what the button does
// depends on where the wallet stands:
//   signed in            → "Save"            device-key PATCH, no popup
//   account, no session  → "Sign & save"     one login signature, then PATCH
//   no account yet       → "Create profile"  one register signature, then PATCH
// The wallet is asked at most once, and only when the user commits.
// The picture is an inscription the wallet holds (`ord://<inscriptionId>`).

import React from 'react'
import { User } from 'lucide-react'
import { cn } from '@/lib/utils'
import useWalletInscriptions from '@/utils/hooks/useWalletInscriptions'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { TwetchError } from '../lib/twetch-api'
import {
	bip322Capable,
	classifyLoginError,
	classifyRegisterError,
	saveProfileWithWallet,
	registerWithWallet,
	type TwetchIdentity
} from '../lib/twetch-auth'
import { loadDeviceKey } from '../lib/twetch-crypto'
import { signMessageWithWallet } from '../lib/wallet-signature'
import readOwProfile from '../lib/ow-profile'
import { resolveTwetchImage } from '../lib/twetch-media'
import { sessionDeviceKey, useTwetch } from '../lib/twetch-store'
import { twetchSignedPatch } from '../lib/twetch-transport'
import InscriptionPicker from './inscription-picker'
import TwetchTurnstile from './twetch-turnstile'
import { identityForAddress } from '../lib/identity-match'

const NAME_MAX = 50
const BIO_MAX = 160

export interface EditableTwetchProfile {
	name?: string | null
	description?: string | null
	icon?: string | null
}

/// What the save produced — enough for the caller to show it immediately
/// instead of re-reading a profile the browser still has cached.
export interface SavedTwetchProfile {
	id: number
	name: string
	description: string
	icon?: string | null
}

function isWalletRejection(e: unknown): boolean {
	return /reject|declin|cancel|denied/i.test(String((e as { message?: string })?.message ?? ''))
}

export default function TwetchProfileEditor({
	open,
	onOpenChange,
	address,
	hasAccount,
	profile,
	onSaved
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	address: string
	/// Whether Twetch already knows this wallet (the identity index found it).
	hasAccount: boolean
	profile: EditableTwetchProfile | null | undefined
	onSaved: (saved: SavedTwetchProfile) => void
}) {
	const storedIdentity = useTwetch((s) => s.identity)
	const setIdentity = useTwetch((s) => s.setIdentity)
	const identity = identityForAddress(storedIdentity, address)
	const signedIn = identity !== null && sessionDeviceKey(identity) !== null
	// The index can miss an account; a register refusal flips this back.
	const [accountExists, setAccountExists] = React.useState(hasAccount)
	const mode: 'save' | 'sign' | 'create' = signedIn ? 'save' : accountExists ? 'sign' : 'create'

	// A wallet that already has an Ordinals Wallet profile starts with it: the
	// same name and picture, so signing up here is a confirmation, not a form.
	// Per field, and only where Twetch holds nothing — an existing Twetch name
	// or picture is never overwritten by OW's.
	const [ow] = React.useState(readOwProfile)
	const [name, setName] = React.useState(profile?.name?.trim() || ow.name || '')
	const [bio, setBio] = React.useState(profile?.description ?? '')
	const [icon, setIcon] = React.useState<string | null>(
		profile?.icon?.trim() ? null : ow.iconRef
	)
	const [busy, setBusy] = React.useState<'wallet' | 'saving' | null>(null)
	const [error, setError] = React.useState<string | null>(null)
	const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null)
	const [turnstileFailed, setTurnstileFailed] = React.useState(false)
	const [turnstileKey, setTurnstileKey] = React.useState(0)

	const { data: inscriptions, isLoading } = useWalletInscriptions(open ? address : undefined)
	const trimmedName = name.trim()
	const nameChanged = trimmedName !== (profile?.name ?? '')
	const bioChanged = bio !== (profile?.description ?? '')
	const changed = nameChanged || bioChanged || icon !== null
	const capable = bip322Capable(address)
	const verifying = mode === 'create' && !turnstileToken && !turnstileFailed
	const canSubmit =
		capable &&
		busy === null &&
		trimmedName.length > 0 &&
		!verifying &&
		!(mode === 'create' && turnstileFailed) &&
		(mode === 'create' || changed)

	const previewSrc = resolveTwetchImage(icon ?? profile?.icon)

	const submit = async () => {
		setError(null)
		let session: TwetchIdentity | null = identity
		let registeredName: string | null = null
		try {
			const signMessage = signMessageWithWallet
			if (mode === 'sign') {
				// No session: the wallet signs THIS edit. One prompt, and it works
				// for a wallet linked to a seed-rooted Twetch account too, which
				// Twetch never grants a full session.
				const edit = {
					...(nameChanged ? { name: trimmedName } : {}),
					...(bioChanged ? { description: bio } : {}),
					...(icon ? { icon } : {})
				}
				if (Object.keys(edit).length === 0) {
					onOpenChange(false)
					return
				}
				setBusy('wallet')
				const saved = await saveProfileWithWallet({ address, edit, signMessage })
				onSaved({
					id: saved.id,
					name: saved.name ?? trimmedName,
					description: saved.description ?? bio,
					icon: icon ? saved.icon ?? icon : profile?.icon
				})
				onOpenChange(false)
				return
			}
			if (mode === 'create') {
				setBusy('wallet')
				session = await registerWithWallet({
					handle: trimmedName,
					address,
					turnstileToken: turnstileToken ?? '',
					signMessage
				})
				registeredName = trimmedName
				setIdentity(session)
			}
			const deviceKey = loadDeviceKey()
			if (!session || !deviceKey) {
				throw new Error('Twetch could not open a session for this wallet.')
			}
			const patch = {
				...(nameChanged && registeredName !== trimmedName ? { name: trimmedName } : {}),
				...(bioChanged ? { description: bio } : {}),
				// Sent only when a new inscription was picked: an unchanged save
				// must not clear a verified NFT picture set on Twetch.
				...(icon ? { icon } : {})
			}
			let row: { icon?: string | null } | null = null
			if (Object.keys(patch).length > 0) {
				setBusy('saving')
				row = await twetchSignedPatch<{ icon?: string | null }>(`/v1/users/${session.uid}`, patch, {
					uid: session.uid,
					deviceKey
				})
			}
			setIdentity({ ...session, handle: trimmedName })
			onSaved({
				id: session.uid,
				name: trimmedName,
				description: bio,
				// The server's canonical ref when it changed; otherwise untouched.
				icon: icon ? row?.icon ?? icon : profile?.icon
			})
			onOpenChange(false)
		} catch (e) {
			if (isWalletRejection(e)) {
				setError('You declined the signature. Nothing was changed.')
			} else if (mode === 'create') {
				const kind = classifyRegisterError(e)
				if (kind === 'address-registered') {
					setAccountExists(true)
					setError('This wallet already has a Twetch profile. Sign to save your changes to it.')
				} else if (kind === 'turnstile') {
					// The token is single-use; a refusal has spent it.
					setTurnstileToken(null)
					setTurnstileFailed(false)
					setTurnstileKey((k) => k + 1)
					setError("Twetch couldn't verify this browser. Give it a moment, then try again.")
				} else if (kind === 'handle-taken') {
					setError('That name is taken. Pick another.')
				} else {
					setError((e as Error)?.message || "Couldn't create the profile.")
				}
			} else if (mode === 'sign' && classifyLoginError(e) === 'no-account') {
				setAccountExists(false)
				setError(null)
			} else if (e instanceof TwetchError && e.status === 401) {
				useTwetch.getState().signOut()
				setError('Your Twetch session ended. Sign once more to save.')
			} else {
				setError((e as Error)?.message || "Couldn't save the profile.")
			}
		} finally {
			setBusy(null)
		}
	}

	const actionLabel =
		busy === 'wallet'
			? 'Check wallet…'
			: busy === 'saving'
			? 'Saving…'
			: verifying
			? 'Verifying…'
			: mode === 'create'
			? 'Create profile'
			: mode === 'sign'
			? 'Sign & save'
			: 'Save'

	const hint = !capable
		? 'Connect a mainnet bc1 wallet that can sign messages to edit a profile.'
		: mode === 'create' && turnstileFailed
		? "New profiles can't be verified from this site right now. Existing profiles can still be edited."
		: mode === 'sign'
		? 'Your wallet signs this change to prove it’s you. No transaction, no fee.'
		: null

	const field =
		'w-full rounded-xl bg-secondary/60 px-3.5 py-3 text-base font-normal text-foreground outline-none ring-1 ring-transparent transition-shadow placeholder:text-muted-foreground focus-visible:ring-primary sm:text-sm'

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				data-testid="alkanes-twetch-profile-editor"
				// `alkanes-ink`: the dialog portals out of `.alkanes-surface`, and
				// without it every token falls back to the site theme.
				className="alkanes-ink flex max-h-[92dvh] flex-col gap-0 overflow-hidden rounded-3xl border-0 bg-card p-0 shadow-2xl max-sm:bottom-0 max-sm:top-auto max-sm:max-w-none max-sm:translate-y-0 max-sm:rounded-b-none sm:max-w-md"
			>
				<form
					className="flex min-h-0 flex-1 flex-col"
					onSubmit={(e) => {
						e.preventDefault()
						if (canSubmit) void submit()
					}}
				>
					<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-2 pt-5">
						{/* Live preview: the header the rest of the app will show. */}
						<div className="flex items-center gap-3.5 pr-8">
							<span className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-full bg-secondary">
								{previewSrc ? (
									<img
										key={previewSrc}
										src={previewSrc}
										alt=""
										className="size-full object-cover [image-rendering:pixelated]"
									/>
								) : (
									<User className="size-6 text-muted-foreground" aria-hidden="true" />
								)}
							</span>
							<div className="min-w-0">
								<DialogTitle className="truncate text-lg font-bold leading-tight">
									{trimmedName || 'Your name'}
								</DialogTitle>
								<DialogDescription className="sr-only">
									{mode === 'create' ? 'Create your Twetch profile' : 'Edit your Twetch profile'}
								</DialogDescription>
							</div>
						</div>

						<div className="mt-5 grid gap-4">
							<label className="grid gap-1.5 text-xs font-semibold text-muted-foreground">
								Name
								<input
									data-testid="alkanes-twetch-profile-name"
									value={name}
									maxLength={NAME_MAX}
									onChange={(e) => setName(e.target.value)}
									placeholder="What should people call you?"
									autoComplete="off"
									autoCapitalize="none"
									spellCheck={false}
									className={field}
								/>
							</label>
							<label className="grid gap-1.5 text-xs font-semibold text-muted-foreground">
								<span className="flex justify-between">
									Bio
									<span
										className={cn(
											'font-normal tabular-nums',
											bio.length >= BIO_MAX && 'text-warning-ink'
										)}
									>
										{bio.length}/{BIO_MAX}
									</span>
								</span>
								<textarea
									data-testid="alkanes-twetch-profile-bio"
									value={bio}
									maxLength={BIO_MAX}
									rows={3}
									onChange={(e) => setBio(e.target.value)}
									placeholder="What do you trade?"
									className={cn(field, 'resize-none leading-relaxed')}
								/>
							</label>
							<fieldset className="min-w-0">
								<legend className="mb-1.5 flex w-full items-baseline justify-between text-xs font-semibold text-muted-foreground">
									Picture
									<span className="font-normal">An inscription you hold</span>
								</legend>
								<InscriptionPicker
									inscriptions={inscriptions}
									loading={isLoading}
									value={icon}
									onChange={setIcon}
								/>
							</fieldset>
						</div>
					</div>

					{/* Thumb-pinned action: the one thing this screen is for. */}
					<div className="shrink-0 border-t border-border/60 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3.5">
						{/* The human check lives HERE, in the pinned footer: when Cloudflare
						    asks for a tap it is on screen, never pushed below the picture
						    grid where nobody would find it. */}
						{mode === 'create' && capable && !turnstileFailed && (
							// Spacing only once Cloudflare actually draws a challenge.
							<div className="flex justify-center [&:has(iframe)]:mb-3">
								<TwetchTurnstile
									appearance="interaction-only"
									resetKey={turnstileKey}
									onToken={setTurnstileToken}
									onError={() => {
										setTurnstileFailed(true)
										setTurnstileToken(null)
									}}
								/>
							</div>
						)}
						{(error || hint) && (
							<p
								role={error ? 'alert' : undefined}
								data-testid="alkanes-twetch-profile-note"
								className={cn(
									'mb-3 text-xs leading-relaxed',
									error ? 'text-destructive-ink' : 'text-muted-foreground'
								)}
							>
								{error ?? hint}
							</p>
						)}
						<button
							type="submit"
							data-testid="alkanes-twetch-profile-save"
							disabled={!canSubmit}
							className="alkanes-touch h-12 w-full rounded-2xl bg-primary text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:bg-secondary disabled:text-muted-foreground"
						>
							{actionLabel}
						</button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}
